// Styles
import { makeStyles } from '@material-ui/core/styles';
import { Menu, MenuItem } from '@material-ui/core';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { headerSearch, unoTheme } from '../../../assets/styles/unoTheme';
import MyTable from '../../utils/MyTable';
import MySelect from '../../utils/MySelect';
import { Pie } from 'react-chartjs-2';
import { applyMaskAmount, formatDataToPieChartPerSizeRightContent, getConsultingId, getMonthName, justNumbers, removeAccents } from "../../utils/utils";
import UnoButton from '../../utils/UnoButton'
import { ArrowDropDown, ArrowDropUp } from '@material-ui/icons';
import _ from 'lodash';

// Data
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import allActions from "../../../actions";
import { FundAPI } from '../../../API/fund';
import { ClientAPI } from '../../../API/client';
import { FundGroupsAPI } from '../../../API/fund_groups';
import XLSX from 'xlsx';
import { downloadBlob, s2ab } from '../../../controllers/ExcelController';

//Sentinelas para as linhas em que o LEFT JOIN não achou benchmark/enquadramento
const NO_BENCHMARK = '__SEM_BENCHMARK__';
const NO_DEVICE = '__SEM_ENQUADRAMENTO__';

//QTD e % do Total saem do mesmo número: a porcentagem é number_of_clients sobre um total que
//não muda com o filtro. Ordenar por qualquer uma das duas é ordenar por number_of_clients —
//que vem como string do pg e precisa ser comparado como número.
const SORT_ACCESSORS = {
    number_of_clients: row => Number(row.number_of_clients),
    percentage: row => Number(row.number_of_clients),
};

const ROMAN_NUMBERS = { I: 1, II: 2, III: 3, IV: 4, V: 5, VI: 6, VII: 7, VIII: 8, IX: 9, X: 10, XI: 11, XII: 12 };

//O xlsx 0.16.7 (build comunitário) só suporta formato numérico, sem estilo
const EXCEL_PERCENT_FORMAT = '0.00"%"';

//device_abbreviation vem como '7, I', '8, II', 'D - RF', 'Imo'. Um localeCompare sozinho erraria
//tanto o número do artigo ('10' antes de '7') quanto o inciso romano ('IX' antes de 'V'), por isso
//a ordenação é manual. Os rótulos sem número (desenquadrados) vão para o fim.
function compareDevices(a, b) {

    const [aArticle, aInciso] = a.split(',').map(part => part.trim());
    const [bArticle, bInciso] = b.split(',').map(part => part.trim());
    const aNumber = parseInt(aArticle);
    const bNumber = parseInt(bArticle);

    if (isNaN(aNumber) || isNaN(bNumber)) {
        if (isNaN(aNumber) && isNaN(bNumber)) return a.localeCompare(b, 'pt-BR');
        return isNaN(aNumber) ? 1 : -1;
    }

    if (aNumber !== bNumber) return aNumber - bNumber;

    return (ROMAN_NUMBERS[aInciso] || 0) - (ROMAN_NUMBERS[bInciso] || 0);
}

// Stylesheet
const useStyles = makeStyles({
    // General Styles
    root: {
        display: 'flex',
        flexDirection: 'column'
    },

    // Header
    headerSearch,

    //O headerSearch estiliza todo .MuiTypography-root como título, por isso o contador é um span
    leftHeader: {
        display: 'flex',
        alignItems: 'baseline',
        gap: '8px',
    },
    counter: {
        fontSize: '12px',
        color: unoTheme.secondaryColor,
        whiteSpace: 'nowrap',
    },
    rightHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    inputSearch: {
        width: '200px',
        background: 'white',
    },

    // Table
    tableColumn: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
    },
    clientCell: {
        width: '125px',
        textAlign: 'center',
        padding: 0
    },

    // Chart Styles
    main: {
        display: 'flex',
        flexDirection: 'row',
        height: '100%',
        maxHeight: '100%',
        maxWidth: '100%',
        width: '100%',
        borderRadius: '10px 0px 0px 10px',
        backgroundColor: 'white',
    },
    divTable: {
        width: '60%',
        maxWidth: '60%',
        height: '100%',

        '& table': {


            '& thead': {
                '& tr': {
                    cursor: 'pointer',
                }
            }
        },

        '& .MuiTableRow-root .MuiTableCell-sizeSmall:last-child': {
            paddingRight: '12px',
        }
    },
    divChart: {
        width: '40%',
        maxWidth: '40%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
    },

});

export default function FundsRanking() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const chartRef = useRef();

    const currentUser = useSelector(state => state.currentUser)
    const [rankingFunds, setRankingFunds] = useState(null);
    const [dataChart, setDataChart] = useState({});

    const [totalClientsNumber, setTotalClientsNumber] = useState(null)

    // Filtros
    const [selectedBenchmarks, setSelectedBenchmarks] = useState([]); //[] = todos
    const [selectedDevice, setSelectedDevice] = useState(''); //'' = todos
    const [selectedFundGroupId, setSelectedFundGroupId] = useState(''); //'' = todos
    const [searchText, setSearchText] = useState(''); //já normalizado e com debounce

    // Ordenação — o default espelha o ORDER BY number_of_clients DESC que o backend devolve
    const [orderBy, setOrderBy] = useState('number_of_clients');
    const [order, setOrder] = useState('desc');

    // Agrupamentos de fundos
    const [fundsGroups, setFundsGroups] = useState([]);
    const [groupFundIds, setGroupFundIds] = useState(null); //Set de fund_id, null = sem filtro
    const groupCacheRef = useRef({}); //evita refetch ao voltar num agrupamento já consultado
    const groupRequestRef = useRef(0); //descarta resposta obsoleta ao trocar de agrupamento
    const searchTimeoutRef = useRef(null);

    // Menu de formato do relatório
    const [anchorEl, setAnchorEl] = useState(null);

    const handleOpenMenuReport = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenuReport = () => {
        setAnchorEl(null);
    };

    // Chart.js Config
    const options = {
        legend: false,
        onClick: (evt, elements) => {
            const data = dataChart.datasets[0].rankingFunds.data;
            let clickedCanvasSlice = elements[0]?._index;

            const getClickedSliceData = data.filter((fundData, index) => index === clickedCanvasSlice)[0];

            dispatch(allActions.rightContentActions.setRightContent({
                path: 'clientsFromFund',
                data: getClickedSliceData[0],
            }))
        },
        onHover: (evt, elements) => {
            const section = elements[0];
            const currentStyle = evt.currentTarget.style;
            currentStyle.cursor = section ? 'pointer' : 'default';
        }
    };

    async function doGetRankingFunds() {
        const response = await FundAPI.getRankingFundsPerClient(getConsultingId(currentUser));
        //const responseVigentFunds = await getRankingFundsPerClientByPeriod();

        if (response.success && response.body) {
            setRankingFunds(response.body.rows);
        } else {
            console.error("RESPONSE ERROR: ", response.error);
        }
    }

    async function doGetTotalClientsNumber() {
        const response = await ClientAPI.listAllClients();

        if (response.success && response.body) {
            setTotalClientsNumber(response.body.rowCount)
        } else {
            console.error("RESPONSE ERROR: ", response.error);
        }
    }

    async function doGetFundsGroups() {
        const response = await FundGroupsAPI.getFundGroups();

        if (response.success) {
            setFundsGroups(response.body.rows ? response.body.rows : []);
        } else {
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true,
                type: "error",
                message: "Falha ao listar agrupamentos de fundos"
            }))
        }
    }

    //Os fundos de um agrupamento não vêm de uma tabela de associação: são os que estão enquadrados
    //hoje nos artigos do grupo. Sem client_id de propósito, porque o ranking é da consultoria toda.
    async function handleChangeFundGroup(fundGroupId) {

        setSelectedFundGroupId(fundGroupId);

        if (!fundGroupId) {
            setGroupFundIds(null);
            return;
        }

        if (groupCacheRef.current[fundGroupId]) {
            setGroupFundIds(groupCacheRef.current[fundGroupId]);
            return;
        }

        const requestId = ++groupRequestRef.current;
        const response = await FundGroupsAPI.getFundGroupFunds(fundGroupId);

        //Usuário trocou de agrupamento enquanto esta resposta vinha
        if (requestId !== groupRequestRef.current) return;

        if (!response.success) {
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true,
                type: "error",
                message: "Falha ao obter os fundos do agrupamento"
            }))
            setSelectedFundGroupId('');
            setGroupFundIds(null);
            return;
        }

        const fundIds = new Set((response.body.rows ? response.body.rows : []).map(row => row.id));
        groupCacheRef.current[fundGroupId] = fundIds;
        setGroupFundIds(fundIds);

        if (!fundIds.size) {
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true,
                type: "warning",
                message: "Nenhum fundo enquadrado atualmente nos artigos deste agrupamento"
            }))
        }
    }

    function changeSearch(e) {

        const value = removeAccents(e.target.value.toUpperCase());

        //O padrão copiado em outras telas passa a função para o clearTimeout, que não cancela nada;
        //guardar o handle numa ref é o que de fato faz o debounce funcionar.
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => setSearchText(value), 400);
    }

    //Índice de busca pré-computado: removeAccents percorre caractere a caractere e rodar isso em
    //centenas de nomes a cada tecla digitada seria desperdício. O CNPJ ora vem mascarado, ora não.
    const indexedFunds = useMemo(() => {

        if (!rankingFunds) return [];

        return rankingFunds.map(row => ({
            row,
            nameKey: removeAccents((row.fund_name || '').toUpperCase()),
            cnpjKey: justNumbers(row.fund_cnpj || ''),
            benchmarkKey: row.benchmark || NO_BENCHMARK,
            deviceKey: row.device_abbreviation || NO_DEVICE,
        }));

    }, [rankingFunds]);

    //As opções saem das próprias linhas, e não das APIs globais: um benchmark que não existe no
    //ranking só produziria uma lista vazia sem explicação.
    const benchmarkOptions = useMemo(() => {

        if (!indexedFunds.length) return [];

        const benchmarks = new Set(indexedFunds.map(fund => fund.benchmarkKey));
        const hasEmpty = benchmarks.delete(NO_BENCHMARK);

        const opts = Array.from(benchmarks)
            .sort((a, b) => a.localeCompare(b, 'pt-BR'))
            .map(benchmark => ({ value: benchmark, label: benchmark }));

        if (hasEmpty) opts.push({ value: NO_BENCHMARK, label: 'Sem benchmark' });

        return opts;

    }, [indexedFunds]);

    const deviceOptions = useMemo(() => {

        if (!indexedFunds.length) return [];

        const devices = new Map();
        indexedFunds.forEach(({ row, deviceKey }) => {
            if (devices.has(deviceKey)) return;
            devices.set(deviceKey, deviceKey === NO_DEVICE
                ? 'Sem enquadramento'
                : deviceKey + (row.article ? ' - ' + row.article : ''));
        });

        const hasEmpty = devices.delete(NO_DEVICE);

        const opts = Array.from(devices.entries())
            .sort((a, b) => compareDevices(a[0], b[0]))
            .map(([value, label]) => ({ value, label }));

        if (hasEmpty) opts.push({ value: NO_DEVICE, label: 'Sem enquadramento' });

        return opts;

    }, [indexedFunds]);

    //E entre os filtros, OU dentro do multi-select de benchmark. Devolve as linhas originais, sem
    //cópia, para o right content continuar recebendo o mesmo objeto de antes.
    const filteredFunds = useMemo(() => {

        if (!indexedFunds.length) return [];

        const benchmarksSet = selectedBenchmarks.length ? new Set(selectedBenchmarks) : null;
        const searchDigits = searchText ? justNumbers(searchText) : '';

        return indexedFunds.filter(fund => {

            if (benchmarksSet && !benchmarksSet.has(fund.benchmarkKey)) return false;
            if (selectedDevice && fund.deviceKey !== selectedDevice) return false;
            if (groupFundIds && !groupFundIds.has(fund.row.fund_id)) return false;

            if (searchText) {
                const matchName = fund.nameKey.includes(searchText);
                const matchCnpj = searchDigits !== '' && fund.cnpjKey.includes(searchDigits);
                if (!matchName && !matchCnpj) return false;
            }

            return true;

        }).map(fund => fund.row);

    }, [indexedFunds, selectedBenchmarks, selectedDevice, groupFundIds, searchText]);

    const sortedFunds = useMemo(() => {
        if (!orderBy || !SORT_ACCESSORS[orderBy]) return filteredFunds;
        return _.orderBy(filteredFunds, [SORT_ACCESSORS[orderBy]], [order]);
    }, [filteredFunds, orderBy, order]);

    const SortIcon = ({ column }) => {
        const isActive = orderBy === column;

        if (!isActive) {
            return <ArrowDropDown style={{ color: '#ccc' }} />;
        }

        return order === 'asc' ? (
            <ArrowDropUp style={{ color: unoTheme.mainColor }} />
        ) : (
            <ArrowDropDown style={{ color: unoTheme.mainColor }} />
        );
    };

    const handleSort = (column) => {
        if (orderBy === column) {
            setOrder(order === 'asc' ? 'desc' : 'asc');
        } else {
            setOrderBy(column);
            setOrder('asc');
        }
    };

    function buildFiltersLabel() {

        const parts = [];

        if (selectedBenchmarks.length) {
            parts.push('Benchmark: ' + selectedBenchmarks
                .map(value => value === NO_BENCHMARK ? 'Sem benchmark' : value)
                .join(', '));
        }

        if (selectedFundGroupId) {
            const group = fundsGroups.find(fundsGroup => String(fundsGroup.id) === String(selectedFundGroupId));
            if (group) parts.push('Agrupamento: ' + group.title);
        }

        if (selectedDevice) {
            const device = deviceOptions.find(option => option.value === selectedDevice);
            if (device) parts.push('Enquadramento: ' + device.label);
        }

        if (searchText) parts.push('Busca: ' + searchText);

        return parts.length ? parts.join('  |  ') : null;
    }

    //A planilha sai do mesmo recorte que está na tela, com os filtros carimbados no topo. QTD e %
    //vão como número para continuarem somáveis no Excel, que não tem tooltip: por isso a descrição
    //completa do artigo, que na tela é só o title da célula, ganha coluna própria.
    function doExcelReport() {

        const now = new Date();
        const periodLabel = getMonthName(now.getMonth() + 1) + '-' + now.getFullYear();
        const fileName = 'Ranking de Fundos ' + periodLabel;
        const filtersLabel = buildFiltersLabel();
        const resolutionName = (sortedFunds.find(row => row.resolution_name) || {}).resolution_name;

        const wb = XLSX.utils.book_new();
        wb.Props = {
            Title: fileName,
            Author: "UNO",
        }

        const sheetName = 'Ranking de Fundos';
        wb.SheetNames.push(sheetName);

        const ws_data = [];
        ws_data.push(['Ranking de Fundos', periodLabel]);
        if (filtersLabel) ws_data.push(['Filtros', filtersLabel]);
        ws_data.push([]);

        //Índice da primeira linha de dado: varia porque a linha de filtros só existe quando há filtro
        const firstDataRow = ws_data.length + 1;

        ws_data.push(['QTD', '% DO TOTAL', 'CNPJ', 'FUNDO',
            'ENQUAD.' + (resolutionName ? ' (' + resolutionName + ')' : ''),
            'ARTIGO', 'BENCHMARK']);

        sortedFunds.forEach(row => {
            ws_data.push([
                Number(row.number_of_clients),
                totalClientsNumber ? (row.number_of_clients / totalClientsNumber) * 100 : '',
                row.fund_cnpj || '',
                row.fund_name || '',
                row.device_abbreviation || '-',
                row.article || '',
                row.benchmark || '-',
            ]);
        });

        const ws = XLSX.utils.aoa_to_sheet(ws_data);

        for (let R = firstDataRow; R < ws_data.length; ++R) {
            const cell = ws[XLSX.utils.encode_cell({ r: R, c: 1 })];
            if (!cell || typeof cell.v !== 'number') continue;
            cell.t = 'n';
            cell.z = EXCEL_PERCENT_FORMAT;
        }

        ws['!cols'] = [{ wch: 8 }, { wch: 12 }, { wch: 20 }, { wch: 60 }, { wch: 16 }, { wch: 45 }, { wch: 16 }];

        wb.Sheets[sheetName] = ws;
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' })

        downloadBlob(new Blob([s2ab(wbout)], { type: "application/octet-stream" }), fileName + '.xlsx');
    }

    function doReport(type) {

        handleCloseMenuReport();

        if (!sortedFunds.length) {
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true,
                type: "warning",
                message: "Nenhum fundo para gerar o relatório com os filtros aplicados"
            }))
            return;
        }

        if (type === 'excel') {
            doExcelReport();
            return;
        }

        dispatch(allActions.reportLoadActions.setReportLoad({
            show: true,
            reportType: 'fundsRanking',
            message: 'Gerando relatório...',
            //O PDF sai do que está na tela, para não divergir do recorte exibido
            reportData: {
                rows: sortedFunds,
                totalClientsNumber: totalClientsNumber,
                filtersLabel: buildFiltersLabel(),
            },
        }))
    }

    useEffect(() => {
        if (!rankingFunds) {
            doGetRankingFunds();
        }
        if (!totalClientsNumber) {
            doGetTotalClientsNumber()
        }
        if (!fundsGroups.length) {
            doGetFundsGroups()
        }
    }, [rankingFunds]);

    useEffect(() => {
        setDataChart(sortedFunds.length
            ? formatDataToPieChartPerSizeRightContent(sortedFunds, 10)
            : {});
    }, [sortedFunds]);

    useEffect(() => () => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    }, []);

    return (
        <div className={classes.root}>

            <div className={classes.headerSearch}>

                <div className={classes.leftHeader}>
                    <Typography variant="h6" gutterBottom align="left">
                        Ranking de fundos
                    </Typography>
                    {rankingFunds ?
                        <span className={classes.counter}>
                            {sortedFunds.length === rankingFunds.length
                                ? rankingFunds.length + ' fundos'
                                : sortedFunds.length + ' de ' + rankingFunds.length + ' fundos'}
                        </span> : null}
                </div>

                <div className={classes.rightHeader}>

                    {/* maxchips segura a altura: o header tem 72px fixos e o container da
                        tabela é dimensionado por calc() em cima disso */}
                    <MySelect
                        multiple
                        maxchips={1}
                        customWidth={'230px'}
                        placeholder={'Todos os benchmarks'}
                        valuesMultiple={selectedBenchmarks}
                        optionsMultiple={benchmarkOptions}
                        onChange={(values) => setSelectedBenchmarks(values)}
                    />

                    <MySelect
                        customWidth={'210px'}
                        value={selectedFundGroupId}
                        onChange={(e) => handleChangeFundGroup(e.target.value)}
                        options={[
                            <option key={'fund-group-all'} value={''}>Todos os agrupamentos</option>,
                        ].concat(fundsGroups.map((row) => (
                            <option key={'fund-group-' + row.id} value={row.id}>{row.title}</option>
                        )))}
                    />

                    <MySelect
                        customWidth={'210px'}
                        value={selectedDevice}
                        onChange={(e) => setSelectedDevice(e.target.value)}
                        options={[
                            <option key={'device-all'} value={''}>Todos os enquadramentos</option>,
                        ].concat(deviceOptions.map((option) => (
                            <option key={'device-' + option.value} value={option.value}>{option.label}</option>
                        )))}
                    />

                    <TextField id="standard-basic"
                        onChange={(event) => changeSearch(event)}
                        className={classes.inputSearch}
                        label="Buscar fundo ou CNPJ..."
                        variant="outlined"
                        size="small" />

                    <UnoButton
                        isIconButton
                        size={'small'}
                        onClick={handleOpenMenuReport}
                        type='primary' />

                    {/* O Menu renderiza em portal, então não interfere no flex do header */}
                    <Menu
                        id="funds-ranking-report-menu"
                        anchorEl={anchorEl}
                        keepMounted
                        open={Boolean(anchorEl)}
                        onClose={handleCloseMenuReport}>
                        <MenuItem onClick={() => doReport('pdf')}>PDF</MenuItem>
                        <MenuItem onClick={() => doReport('excel')}>Excel</MenuItem>
                    </Menu>

                </div>
            </div>

            <div class={'defaultScrollWithHeaderContent'}>
                <div className={classes.main}>
                    {/* <div className={classes.divTable}> */}
                    <MyTable
                        stickyHeader
                        nofixed={true}
                        size="small"
                        selectable="true"
                    >
                        <colgroup>
                            <col width="8%" />
                            <col width="10%" />
                            <col width="18%" />
                            <col width="36%" />
                            <col width="12%" />
                            <col width="16%" />
                        </colgroup>
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.clientCell} onClick={() => handleSort('number_of_clients')}>
                                    <div className={classes.tableColumn}>
                                        QTD <SortIcon column="number_of_clients" />
                                    </div>
                                </TableCell>
                                <TableCell className={classes.clientCell} onClick={() => handleSort('percentage')}>
                                    <div className={classes.tableColumn}>
                                        % do Total <SortIcon column="percentage" />
                                    </div>
                                </TableCell>
                                <TableCell className={classes.clientCell} align="left">CNPJ</TableCell>
                                <TableCell align="left">Fundo</TableCell>
                                <TableCell align="center">
                                    {'Enquad.' + (rankingFunds && rankingFunds[0] && rankingFunds[0].resolution_name
                                        ? ' (' + rankingFunds[0].resolution_name + ')' : '')}
                                </TableCell>
                                <TableCell className={classes.clientCell} align="center">Benchmark</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedFunds.map((row, index) => (
                                <TableRow
                                    key={'ranking-funds-clients-' + index}
                                    onClick={() => dispatch(allActions.rightContentActions.setRightContent({
                                        path: 'clientsFromFund',
                                        data: row,
                                    }))}>
                                    <TableCell className={classes.clientCell} align="left">{row.number_of_clients}</TableCell>
                                    <TableCell className={classes.clientCell} align="left">{applyMaskAmount((row.number_of_clients / totalClientsNumber) * 100) + '%'}</TableCell>
                                    <TableCell className={classes.clientCell} align="left">{row.fund_cnpj}</TableCell>
                                    <TableCell align="left">{row.fund_name}</TableCell>
                                    <TableCell align="center" title={row.article ? row.article : ''}>{row.device_abbreviation || '-'}</TableCell>
                                    <TableCell align="center">{row.benchmark || '-'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </MyTable>
                    {/* </div> */}
                    {/* <div className={classes.divChart}>
                        <Pie data={dataChart} options={options} ref={chartRef} />
                    </div> */}

                </div>
            </div>
        </div >
    );
}
